/**
 * Fold service, auto-fold plugin, and fold management utilities.
 */

import {
	foldEffect,
	foldedRanges,
	foldService,
	unfoldEffect,
} from "@codemirror/language";
import {
	Annotation,
	RangeSet,
	RangeSetBuilder,
	StateField,
	type Extension,
	type StateEffect,
} from "@codemirror/state";
import {
	EditorView,
	GutterMarker,
	gutterLineClass,
	ViewPlugin,
} from "@codemirror/view";
import {
	CONTENT_HEADER_RE,
	DEFINE_RE,
	FOR_CLOSE_RE,
	FOR_HEADER_RE,
	IMPORT_RE,
	META_HEADER_RE,
	parseMarkdownFenceOpen,
	USE_HEADER_RE,
} from "./patterns";
import { magicScanField } from "./scan-state";
import type { FoldSnapshot } from "./types";
import { collectMarkdownMathExcludedLines } from "./build-decorations";
import { collectMarkdownMathBlockRanges } from "./markdown-math/scan";
import { collectMarkdownRichBlockRanges } from "./markdown-rich/scan";

type FoldDoc = Pick<EditorView["state"]["doc"], "lines" | "line" | "lineAt">;

/** Regex matching block openers that close with `::` — used for nesting depth in content blocks */
const BLOCK_OPENER_RE = /^::(define\[|content\[|meta\[|use\[|import\{)/;

/** Fast prefix check: all block headers start with `::` or `` ` `` (fenced code) */
const BLOCK_PREFIX_COLON = "::";
const BLOCK_PREFIX_BACKTICK = "`";
const SYNTAX_FENCE_RE = /^(`{3,})(config|output|inputs)\s*$/;
type SyntaxFenceKind = "config" | "output" | "inputs";

class MagicFoldCloseLineMarker extends GutterMarker {
	elementClass = "cm-magic-fold-close-line";
}

const magicFoldCloseLineMarker = new MagicFoldCloseLineMarker();

export function isMagicFoldCloseLineText(text: string): boolean {
	const trimmed = text.trim();
	return trimmed === "::" || FOR_CLOSE_RE.test(trimmed);
}

function buildMagicFoldCloseLineMarkers(state: EditorView["state"]): RangeSet<GutterMarker> {
	const builder = new RangeSetBuilder<GutterMarker>();
	for (let i = 1; i <= state.doc.lines; i++) {
		const line = state.doc.line(i);
		if (isMagicFoldCloseLineText(line.text)) {
			builder.add(line.from, line.from, magicFoldCloseLineMarker);
		}
	}
	return builder.finish();
}

const magicFoldCloseLineClassField = StateField.define<RangeSet<GutterMarker>>({
	create: buildMagicFoldCloseLineMarkers,
	update(value, tr) {
		return tr.docChanged ? buildMagicFoldCloseLineMarkers(tr.state) : value.map(tr.changes);
	},
});

export const magicFoldGutterLineClass: Extension = [
	magicFoldCloseLineClassField,
	gutterLineClass.from(magicFoldCloseLineClassField),
	EditorView.baseTheme({
		".cm-foldGutter .cm-gutterElement.cm-magic-fold-close-line": {
			color: "transparent",
			pointerEvents: "none",
		},
	}),
];

/**
 * Find the closing `::` for a block that nests other `::` blocks.
 * Returns the closing line (doc.line), or null if not found.
 */
/** Maximum nesting depth for block scanning — prevents runaway on malformed documents */
const MAX_NESTING_DEPTH = 100;

type MarkdownFenceState = { char: "`" | "~"; length: number };

function isMarkdownFenceCloseText(
	trimmed: string,
	fence: MarkdownFenceState,
): boolean {
	return trimmed.length >= fence.length &&
		trimmed === fence.char.repeat(trimmed.length);
}

function buildMagicBlockBodyLineMask(
	state: EditorView["state"],
): boolean[] {
	const doc = state.doc;
	const mask = new Array<boolean>(doc.lines + 2).fill(false);
	const analysis = state.field(magicScanField, false);
	if (!analysis) return mask;
	const markBody = (startLine: number, endLine: number) => {
		for (let i = startLine + 1; i < endLine; i++) {
			mask[i] = true;
		}
	};
	for (const block of analysis.scan.defineBlocks) markBody(block.startLine, block.endLine);
	for (const block of analysis.scan.metaBlocks) markBody(block.startLine, block.endLine);
	for (const block of analysis.scan.contentBlocks) markBody(block.startLine, block.endLine);
	for (const block of analysis.scan.useBlocks) markBody(block.startLine, block.endLine);
	for (const block of analysis.scan.importLines) {
		if (block.endLine > block.startLine) markBody(block.startLine, block.endLine);
	}
	return mask;
}

function updateMarkdownFenceSkip(
	trimmed: string,
	inMagicBlockBody: boolean,
	fence: MarkdownFenceState | null,
): { fence: MarkdownFenceState | null; skipLine: boolean } {
	if (inMagicBlockBody) return { fence, skipLine: false };
	if (fence) {
		return {
			fence: isMarkdownFenceCloseText(trimmed, fence) ? null : fence,
			skipLine: true,
		};
	}
	const opened = parseMarkdownFenceOpen(trimmed);
	if (!opened) return { fence: null, skipLine: false };
	return {
		fence: { char: opened.char, length: opened.length },
		skipLine: true,
	};
}

function findNestedBlockClose(
	state: {
		doc: FoldDoc;
	},
	startLineNum: number,
): { to: number } | null {
	let depth = 0;
	for (let i = startLineNum + 1; i <= state.doc.lines; i++) {
		const nextText = state.doc.line(i).text.trim();
		if (BLOCK_OPENER_RE.test(nextText)) {
			// Guard against runaway on adversarial/deeply-malformed documents
			if (depth < MAX_NESTING_DEPTH) depth++;
		} else if (/^::\s*$/.test(nextText)) {
			if (depth > 0) {
				depth--;
			} else {
				return state.doc.line(i);
			}
		}
	}
	return null;
}

function findForBlockClose(
	state: {
		doc: FoldDoc;
	},
	startLineNum: number,
): { to: number } | null {
	let depth = 0;
	for (let i = startLineNum + 1; i <= state.doc.lines; i++) {
		const nextText = state.doc.line(i).text.trim();
		if (FOR_HEADER_RE.test(nextText)) {
			if (depth < MAX_NESTING_DEPTH) depth++;
			continue;
		}
		if (FOR_CLOSE_RE.test(nextText)) {
			if (depth > 0) {
				depth--;
			} else {
				return state.doc.line(i);
			}
		}
	}
	return null;
}

function findStandaloneBlockRange(
	doc: FoldDoc,
	startLineNum: number,
): { from: number; to: number } | null {
	const startLine = doc.line(startLineNum);
	for (let i = startLineNum + 1; i <= doc.lines; i++) {
		const nextLine = doc.line(i);
		if (nextLine.text.trim() === "::") {
			return { from: startLine.to, to: nextLine.to };
		}
	}
	return null;
}

function findImportBlockRange(
	doc: FoldDoc,
	startLineNum: number,
): { from: number; to: number } | null {
	const startLine = doc.line(startLineNum);
	for (let i = startLineNum + 1; i <= doc.lines; i++) {
		const nextLine = doc.line(i);
		const nextText = nextLine.text.trim();
		if (nextText === "::") {
			return { from: startLine.to, to: nextLine.to };
		}
		if (BLOCK_OPENER_RE.test(nextText)) {
			return null;
		}
	}
	return null;
}

function addSyntaxFenceFoldsInRange(
	doc: FoldDoc,
	startLineNum: number,
	endLineNum: number,
	effects: StateEffect<unknown>[],
	shouldFold: (kind: SyntaxFenceKind) => boolean = () => true,
): void {
	for (let i = startLineNum + 1; i < endLineNum; i++) {
		const line = doc.line(i);
		const match = line.text.trim().match(SYNTAX_FENCE_RE);
		if (!match) continue;

		const kind = match[2] as SyntaxFenceKind;
		const fenceLen = match[1].length;
		const closeFence = "`".repeat(fenceLen);
		for (let j = i + 1; j < endLineNum; j++) {
			if (doc.line(j).text.trim() === closeFence) {
				if (shouldFold(kind)) {
					effects.push(foldEffect.of({ from: line.to, to: doc.line(j).to }));
				}
				i = j;
				break;
			}
		}
	}
}

function orderFoldEffects(
	effects: StateEffect<unknown>[],
): StateEffect<unknown>[] {
	const leading: StateEffect<unknown>[] = [];
	const folds: StateEffect<unknown>[] = [];
	for (const effect of effects) {
		if (effect.is(foldEffect)) {
			folds.push(effect);
		} else {
			leading.push(effect);
		}
	}
	folds.sort((a, b) => {
		const aRange = a.value as { from: number; to: number };
		const bRange = b.value as { from: number; to: number };
		return aRange.from - bRange.from || aRange.to - bRange.to;
	});
	return [...leading, ...folds];
}

function addContentInnerBlockFoldsInRange(
	doc: FoldDoc,
	startLineNum: number,
	endLineNum: number,
	effects: StateEffect<unknown>[],
): void {
	for (let i = startLineNum + 1; i < endLineNum; i++) {
		const line = doc.line(i);
		const trimmed = line.text.trim();
		let range: { from: number; to: number } | null = null;

		if (CONTENT_HEADER_RE.test(trimmed)) {
			const closeLine = findNestedBlockClose({ doc }, i);
			range = closeLine ? { from: line.to, to: closeLine.to } : null;
		} else if (FOR_HEADER_RE.test(trimmed)) {
			const closeLine = findForBlockClose({ doc }, i);
			range = closeLine ? { from: line.to, to: closeLine.to } : null;
		} else if (DEFINE_RE.test(trimmed) || META_HEADER_RE.test(trimmed) || USE_HEADER_RE.test(trimmed)) {
			range = findStandaloneBlockRange(doc, i);
		} else if (IMPORT_RE.test(trimmed)) {
			range = findImportBlockRange(doc, i);
		}

		if (range) {
			effects.push(foldEffect.of(range));
		}
	}
}

function getCachedFoldRange(
	state: EditorView["state"],
	key: string,
): { from: number; to: number } | null {
	return state.field(magicScanField, false)?.foldBlocks.get(key) ?? null;
}

function getCachedFoldRangeStartingOnLine(
	state: EditorView["state"],
	key: string,
	lineNumber: number,
): { from: number; to: number } | null {
	const range = getCachedFoldRange(state, key);
	if (!range) return null;
	const line = state.doc.line(lineNumber);
	if (range.from !== line.to) return null;
	if (range.to <= range.from || range.to > state.doc.length) return null;
	return range;
}

function rangeEndLineNumber(
	doc: FoldDoc,
	currentLineNumber: number,
	range: { to: number },
): number {
	const endLineNumber = doc.lineAt(range.to).number;
	return endLineNumber > currentLineNumber ? endLineNumber : currentLineNumber;
}

export function findFoldRangeStartingOnLine(
	state: EditorView["state"],
	position: number,
): { from: number; to: number } | null {
	const line = state.doc.lineAt(position);
	const folded = foldedRanges(state).iter();
	while (folded.value) {
		if (folded.from === line.to) {
			return { from: folded.from, to: folded.to };
		}
		folded.next();
	}
	return null;
}

function getMarkdownL0FoldRanges(
	state: EditorView["state"],
): Map<string, { from: number; to: number }> {
	const ranges = new Map<string, { from: number; to: number }>();
	const analysis = state.field(magicScanField, false);
	const excludedLines = analysis
		? collectMarkdownMathExcludedLines(analysis.scan)
		: new Set<number>();
	const mathRanges = collectMarkdownMathBlockRanges(state.doc, excludedLines);
	for (const range of mathRanges) {
		if (range.endLine <= range.startLine) continue;
		ranges.set(`markdown-math:${range.startLine}-${range.endLine}`, {
			from: state.doc.line(range.startLine).to,
			to: state.doc.line(range.endLine).to,
		});
	}

	const occupiedLines = new Set<number>();
	for (const range of mathRanges) {
		for (let line = range.startLine; line <= range.endLine; line += 1) {
			occupiedLines.add(line);
		}
	}
	const richRanges = collectMarkdownRichBlockRanges(
		state.doc,
		excludedLines,
		occupiedLines,
	);
	for (const range of richRanges) {
		if (range.endLine <= range.startLine) continue;
		ranges.set(
			`markdown-rich:${range.kind}:${range.startLine}-${range.endLine}`,
			{
				from: state.doc.line(range.startLine).to,
				to: state.doc.line(range.endLine).to,
			},
		);
	}
	return ranges;
}

function getMarkdownL0FoldRange(
	state: EditorView["state"],
	lineNumber: number,
): { from: number; to: number } | null {
	for (const range of getMarkdownL0FoldRanges(state).values()) {
		if (state.doc.lineAt(range.from).number === lineNumber) return range;
	}
	return null;
}

function getAutoFoldRanges(
	state: EditorView["state"],
): Map<string, { from: number; to: number }> {
	return new Map([
		...state.field(magicScanField).foldBlocks,
		...getMarkdownL0FoldRanges(state),
	]);
}

/* ── Fold service for fenced output/config/inputs blocks inside Magic blocks ── */
export const magicBlockFoldService = foldService.of((state, lineStart) => {
	const line = state.doc.lineAt(lineStart);
	const text = line.text.trim();

	const markdownL0FoldRange = getMarkdownL0FoldRange(state, line.number);
	if (markdownL0FoldRange) return markdownL0FoldRange;

	// Fast prefix check — all foldable headers start with `::` or `` ` ``
	if (text.startsWith(BLOCK_PREFIX_BACKTICK)) {
		// Fold ```output, ```config, or ```inputs blocks (dynamic fence length)
		const fenceMatch = text.match(SYNTAX_FENCE_RE);
		if (fenceMatch) {
			const fenceLen = fenceMatch[1].length;
			const closeFence = "`".repeat(fenceLen);
			for (let i = line.number + 1; i <= state.doc.lines; i++) {
				const nextLine = state.doc.line(i);
				if (nextLine.text.trim() === closeFence) {
					return { from: line.to, to: nextLine.to };
				}
			}
		}
		return null;
	}

	if (!text.startsWith(BLOCK_PREFIX_COLON)) return null;

	// ::content[id] blocks (nesting-aware — must check before generic :: close search)
	if (CONTENT_HEADER_RE.test(text)) {
		const closeLine = findNestedBlockClose(state, line.number);
		if (closeLine) {
			return { from: line.to, to: closeLine.to };
		}
		return null;
	}

	// ::for[id]{...} ... ::endfor blocks
	if (FOR_HEADER_RE.test(text)) {
		const closeLine = findForBlockClose(state, line.number);
		if (closeLine) {
			return { from: line.to, to: closeLine.to };
		}
		return null;
	}

	// ::define, ::meta, ::use — always close with standalone `::`
	if (DEFINE_RE.test(text) || META_HEADER_RE.test(text) || USE_HEADER_RE.test(text)) {
		for (let i = line.number + 1; i <= state.doc.lines; i++) {
			const nextLine = state.doc.line(i);
			if (nextLine.text.trim() === "::") {
				return { from: line.to, to: nextLine.to };
			}
		}
	}

	// ::import is a single-line directive but can optionally have a body closed by `::`
	// Stop scanning if we hit another block opener — that :: belongs to the block below
	if (IMPORT_RE.test(text)) {
		for (let i = line.number + 1; i <= state.doc.lines; i++) {
			const nextLine = state.doc.line(i);
			const nextText = nextLine.text.trim();
			if (nextText === "::") {
				return { from: line.to, to: nextLine.to };
			}
			if (BLOCK_OPENER_RE.test(nextText)) {
				return null;
			}
		}
	}

	return null;
});

/* ── Fold management (used by autoFoldPlugin + useCodeMirror replaceContent) ── */

/** Marks a transaction as a full document replacement so auto-fold logic can skip new-block folding. */
export const fullDocumentReplaceAnnotation = Annotation.define<boolean>();

/** Scan the document to capture which foldable blocks are currently unfolded */
export function getFoldSnapshot(view: EditorView): FoldSnapshot {
	const doc = view.state.doc;
	const folded = foldedRanges(view.state);

	const foldedFroms = new Set<number>();
	const iter = folded.iter();
	while (iter.value) {
		foldedFroms.add(iter.from);
		iter.next();
	}

	const unfolded = new Set<string>();
	let currentDefId: string | null = null;
	let forNesting = 0;
	let currentForId: string | null = null;
	const magicBlockBodyLines = buildMagicBlockBodyLineMask(view.state);
	let markdownFence: MarkdownFenceState | null = null;

	for (let i = 1; i <= doc.lines; i++) {
		const line = doc.line(i);
		const trimmed = line.text.trim();
		if (currentForId && !currentDefId) {
			const subMatch = trimmed.match(SYNTAX_FENCE_RE);
			if (subMatch) {
				if (subMatch[2] !== "output") {
					const subKey = `for:${currentForId}:${subMatch[2]}`;
					if (!foldedFroms.has(line.to)) unfolded.add(subKey);
				}
				const subFenceLen = subMatch[1].length;
				const subCloseFence = "`".repeat(subFenceLen);
				for (let j = i + 1; j <= doc.lines; j++) {
					if (doc.line(j).text.trim() === subCloseFence) {
						i = j;
						break;
					}
				}
				continue;
			}
		}
		const fenceUpdate = updateMarkdownFenceSkip(
			trimmed,
			magicBlockBodyLines[i] === true,
			markdownFence,
		);
		markdownFence = fenceUpdate.fence;
		if (fenceUpdate.skipLine) continue;

		// Track ::meta[domain] blocks
		const metaMatch = trimmed.match(META_HEADER_RE);
		if (metaMatch) {
			const metaKey = `meta:${metaMatch[1]}`;
			if (!foldedFroms.has(line.to)) unfolded.add(metaKey);
			for (let j = i + 1; j <= doc.lines; j++) {
				const jLine = doc.line(j);
				const jText = jLine.text.trim();
				if (/^::\s*$/.test(jText)) {
					i = j;
					break;
				}
				const subM = jText.match(SYNTAX_FENCE_RE);
				if (subM && subM[2] !== "output") {
					const subKey = `meta:${metaMatch[1]}:${subM[2]}`;
					if (!foldedFroms.has(jLine.to)) unfolded.add(subKey);
				}
			}
			continue;
		}

		// Track ::use[id]{...} blocks (process sub-fences like ::define)
		const useMatch = trimmed.match(USE_HEADER_RE);
		if (useMatch) {
			const useKey = `use:${useMatch[1]}`;
			if (!foldedFroms.has(line.to)) unfolded.add(useKey);
			// Process inner lines to track config sub-fences (NOT output — output always re-folds)
			for (let j = i + 1; j <= doc.lines; j++) {
				const jLine = doc.line(j);
				const jText = jLine.text.trim();
				if (/^::\s*$/.test(jText)) {
					i = j;
					break;
				}
				const subM = jText.match(SYNTAX_FENCE_RE);
				if (subM && subM[2] !== "output") {
					const subKey = `use:${useMatch[1]}:${subM[2]}`;
					if (!foldedFroms.has(jLine.to)) unfolded.add(subKey);
				}
			}
			continue;
		}

		// Track ::import{...} blocks (block form)
		if (IMPORT_RE.test(trimmed)) {
			const nsMatch = trimmed.match(/ns="([^"]+)"/);
			const importRange = findImportBlockRange(doc, i);
			if (nsMatch && importRange) {
				const importKey = `import:${nsMatch[1]}`;
				if (!foldedFroms.has(line.to)) unfolded.add(importKey);
			}
			for (let j = i + 1; j <= doc.lines; j++) {
				const jLine = doc.line(j);
				const jText = jLine.text.trim();
				if (/^::\s*$/.test(jText)) {
					i = j;
					break;
				}
				// Hit a block opener before :: → single-line import, don't advance i
				if (BLOCK_OPENER_RE.test(jText)) break;
				const subM = jText.match(SYNTAX_FENCE_RE);
				if (subM && subM[2] !== "output" && nsMatch) {
					const subKey = `import:${nsMatch[1]}:${subM[2]}`;
					if (!foldedFroms.has(jLine.to)) unfolded.add(subKey);
				}
			}
			continue;
		}

		// Track ::content[id] blocks (nesting-aware)
		const contentMatch = trimmed.match(CONTENT_HEADER_RE);
		if (contentMatch) {
			const contentKey = `content:${contentMatch[1]}`;
			if (!foldedFroms.has(line.to)) unfolded.add(contentKey);
			// Skip with nesting awareness
			let depth = 0;
			for (let j = i + 1; j <= doc.lines; j++) {
				const jText = doc.line(j).text.trim();
				if (BLOCK_OPENER_RE.test(jText)) depth++;
				else if (/^::\s*$/.test(jText)) {
					if (depth > 0) {
						depth--;
					} else {
						i = j;
						break;
					}
				}
			}
			continue;
		}

		const defMatch = trimmed.match(DEFINE_RE);
		if (defMatch) {
			currentDefId = defMatch[1];
			if (!foldedFroms.has(line.to)) unfolded.add(currentDefId);
			continue;
		}
		if (trimmed === "::" && currentDefId) {
			currentDefId = null;
			continue;
		}

		if (currentDefId) {
			const subMatch = trimmed.match(SYNTAX_FENCE_RE);
			// Only track editable sub-fences; output fences always re-fold after execution
			if (subMatch && subMatch[2] !== "output") {
				const subKey = `${currentDefId}:${subMatch[2]}`;
				if (!foldedFroms.has(line.to)) unfolded.add(subKey);
			}
		}

		// Track ::for block fold state
		const forMatch = trimmed.match(FOR_HEADER_RE);
		if (forMatch) {
			if (forNesting === 0) currentForId = forMatch[1];
			forNesting++;
			const forKey = `for:${forMatch[1]}`;
			if (!foldedFroms.has(line.to)) unfolded.add(forKey);
			continue;
		}
		if (FOR_CLOSE_RE.test(trimmed) && forNesting > 0) {
			forNesting--;
			if (forNesting === 0) currentForId = null;
			continue;
		}

		// For-block output fences always re-fold (not tracked in snapshot)
	}

	for (const [key, range] of getMarkdownL0FoldRanges(view.state)) {
		if (!foldedFroms.has(range.from)) unfolded.add(key);
	}

	return { unfolded };
}

/**
 * Compute fold/unfold effects for the current document so callers can batch
 * document replacement and fold restoration into a single transaction.
 */
export function buildFoldEffects(
	view: Pick<EditorView, "state">,
	snapshot: FoldSnapshot,
) {
	const doc = view.state.doc;

	// Clear existing folds
	const folded = foldedRanges(view.state);
	const effects: StateEffect<unknown>[] = [];
	const foldIter = folded.iter();
	while (foldIter.value) {
		effects.push(unfoldEffect.of({ from: foldIter.from, to: foldIter.to }));
		foldIter.next();
	}

	// Compute fresh fold ranges
	let currentDefId: string | null = null;
	let forNesting = 0;
	let currentForId: string | null = null;
	const magicBlockBodyLines = buildMagicBlockBodyLineMask(view.state);
	let markdownFence: MarkdownFenceState | null = null;

	for (const [key, range] of getMarkdownL0FoldRanges(view.state)) {
		if (!snapshot.unfolded.has(key)) {
			effects.push(foldEffect.of(range));
		}
	}

	for (let i = 1; i <= doc.lines; i++) {
		const line = doc.line(i);
		const trimmed = line.text.trim();
		if (currentForId && !currentDefId) {
			const subMatch = trimmed.match(SYNTAX_FENCE_RE);
			if (subMatch) {
				if (!snapshot.unfolded.has(`for:${currentForId}:${subMatch[2]}`)) {
					const subFenceLen = subMatch[1].length;
					const subCloseFence = "`".repeat(subFenceLen);
					for (let j = i + 1; j <= doc.lines; j++) {
						if (doc.line(j).text.trim() === subCloseFence) {
							effects.push(foldEffect.of({ from: line.to, to: doc.line(j).to }));
							i = j;
							break;
						}
					}
				}
				continue;
			}
		}
		const fenceUpdate = updateMarkdownFenceSkip(
			trimmed,
			magicBlockBodyLines[i] === true,
			markdownFence,
		);
		markdownFence = fenceUpdate.fence;
		if (fenceUpdate.skipLine) continue;

		// Fold ::meta[domain] blocks
		const metaMatch = trimmed.match(META_HEADER_RE);
		if (metaMatch) {
			const metaKey = `meta:${metaMatch[1]}`;
			const range =
				getCachedFoldRangeStartingOnLine(view.state, metaKey, line.number) ??
				findStandaloneBlockRange(doc, line.number);
			if (range) {
				const endLineNumber = rangeEndLineNumber(doc, line.number, range);
				addSyntaxFenceFoldsInRange(
					doc,
					line.number,
					endLineNumber,
					effects,
					(kind) =>
						kind === "output" ||
						!snapshot.unfolded.has(`meta:${metaMatch[1]}:${kind}`),
				);
				if (!snapshot.unfolded.has(metaKey)) {
					effects.push(foldEffect.of(range));
				}
				i = endLineNumber;
			}
			continue;
		}

		// Fold ::use[id]{...} blocks + sub-fences
		const useMatch = trimmed.match(USE_HEADER_RE);
		if (useMatch) {
			const useKey = `use:${useMatch[1]}`;
			const range =
				getCachedFoldRangeStartingOnLine(view.state, useKey, line.number) ??
				findStandaloneBlockRange(doc, line.number);
			if (range) {
				const endLineNumber = rangeEndLineNumber(doc, line.number, range);
				addSyntaxFenceFoldsInRange(
					doc,
					line.number,
					endLineNumber,
					effects,
					(kind) =>
						kind === "output" ||
						!snapshot.unfolded.has(`use:${useMatch[1]}:${kind}`),
				);
				if (!snapshot.unfolded.has(useKey)) {
					effects.push(foldEffect.of(range));
				}
				i = endLineNumber;
			}
			continue;
		}

		// Fold ::import{...} blocks (block form)
		if (IMPORT_RE.test(trimmed)) {
			const nsMatch = trimmed.match(/ns="([^"]+)"/);
			const importKey = nsMatch ? `import:${nsMatch[1]}` : null;
			const range = importKey
				? getCachedFoldRangeStartingOnLine(view.state, importKey, line.number) ??
					findImportBlockRange(doc, line.number)
				: findImportBlockRange(doc, line.number);
			if (range) {
				const endLineNumber = rangeEndLineNumber(doc, line.number, range);
				addSyntaxFenceFoldsInRange(
					doc,
					line.number,
					endLineNumber,
					effects,
					(kind) =>
						kind === "output" ||
						!importKey ||
						!snapshot.unfolded.has(`${importKey}:${kind}`),
				);
				if (!importKey || !snapshot.unfolded.has(importKey)) {
					effects.push(foldEffect.of(range));
				}
				i = endLineNumber;
			} else if (importKey && snapshot.unfolded.has(importKey)) {
				// Import block is expanded — fold sub-fences individually.
				for (let j = i + 1; j <= doc.lines; j++) {
					const jLine = doc.line(j);
					const jText = jLine.text.trim();
					if (/^::\s*$/.test(jText)) {
						i = j;
						break;
					}
					if (BLOCK_OPENER_RE.test(jText)) break;
					const subM = jText.match(SYNTAX_FENCE_RE);
					if (!subM) continue;

					const subKey = nsMatch ? `import:${nsMatch[1]}:${subM[2]}` : null;
					if (subM[2] !== "output" && subKey && snapshot.unfolded.has(subKey)) {
						continue;
					}

					const subFenceLen = subM[1].length;
					const subCloseFence = "`".repeat(subFenceLen);
					for (let k = j + 1; k <= doc.lines; k++) {
						if (doc.line(k).text.trim() === subCloseFence) {
							effects.push(
								foldEffect.of({ from: jLine.to, to: doc.line(k).to }),
							);
							j = k;
							break;
						}
					}
				}
			}
			continue;
		}

		// Fold ::content[id] blocks (nesting-aware)
		const contentMatch = trimmed.match(CONTENT_HEADER_RE);
		if (contentMatch) {
			const contentKey = `content:${contentMatch[1]}`;
			const closeLine = findNestedBlockClose({ doc }, line.number);
			if (closeLine) {
				addContentInnerBlockFoldsInRange(doc, line.number, doc.lineAt(closeLine.to).number, effects);
				addSyntaxFenceFoldsInRange(doc, line.number, doc.lineAt(closeLine.to).number, effects);
			}
			if (!snapshot.unfolded.has(contentKey)) {
				const range =
					getCachedFoldRangeStartingOnLine(view.state, contentKey, line.number) ??
					(closeLine ? { from: line.to, to: closeLine.to } : null);
				if (range) {
					effects.push(foldEffect.of(range));
					i = rangeEndLineNumber(doc, line.number, range);
				}
			} else if (closeLine) {
				i = rangeEndLineNumber(doc, line.number, closeLine);
			}
			continue;
		}

		const defMatch = trimmed.match(DEFINE_RE);
		if (defMatch) {
			currentDefId = defMatch[1];
			const range =
				getCachedFoldRangeStartingOnLine(view.state, currentDefId, line.number) ??
				findStandaloneBlockRange(doc, line.number);
			if (range) {
				const endLineNumber = rangeEndLineNumber(doc, line.number, range);
				addSyntaxFenceFoldsInRange(
					doc,
					line.number,
					endLineNumber,
					effects,
					(kind) =>
						kind === "output" ||
						!snapshot.unfolded.has(`${defMatch[1]}:${kind}`),
				);
				if (!snapshot.unfolded.has(currentDefId)) {
					effects.push(foldEffect.of(range));
				}
				currentDefId = null;
				i = endLineNumber;
			}
			continue;
		}
		if (trimmed === "::" && currentDefId) {
			currentDefId = null;
			continue;
		}

		if (currentDefId) {
			const subMatch = trimmed.match(SYNTAX_FENCE_RE);
			if (
				subMatch &&
				!snapshot.unfolded.has(`${currentDefId}:${subMatch[2]}`)
			) {
				const subFenceLen = subMatch[1].length;
				const subCloseFence = "`".repeat(subFenceLen);
				for (let j = i + 1; j <= doc.lines; j++) {
					if (doc.line(j).text.trim() === subCloseFence) {
						effects.push(foldEffect.of({ from: line.to, to: doc.line(j).to }));
						break;
					}
				}
			}
		}

		// Fold ::for blocks
		const forMatch = trimmed.match(FOR_HEADER_RE);
		if (forMatch) {
			if (forNesting === 0) currentForId = forMatch[1];
			forNesting++;
			if (!snapshot.unfolded.has(`for:${forMatch[1]}`)) {
				const range =
					getCachedFoldRangeStartingOnLine(view.state, `for:${forMatch[1]}`, line.number) ??
					(() => {
						const closeLine = findForBlockClose({ doc }, line.number);
						return closeLine
							? { from: line.to, to: closeLine.to }
							: null;
					})();
				if (range) {
					effects.push(foldEffect.of(range));
				}
			}
			continue;
		}
		if (FOR_CLOSE_RE.test(trimmed) && forNesting > 0) {
			forNesting--;
			if (forNesting === 0) currentForId = null;
			continue;
		}

	}

	return orderFoldEffects(effects);
}

/**
 * Fold all ::define blocks AND their sub-blocks.
 * Blocks whose key is in `snapshot.unfolded` are left open.
 */
export function foldAllBlocks(view: EditorView, snapshot: FoldSnapshot) {
	const effects = buildFoldEffects(view, snapshot);
	if (effects.length > 0) view.dispatch({ effects });
}

/* ── Helpers ── */

/* ── Auto-fold: initial load + newly created define blocks ── */

export const autoFoldPlugin = ViewPlugin.define((view) => {
	// Track known block keys so we can detect new ones.
	// Read from the shared StateField instead of scanning independently.
	const knownKeys = new Set<string>();
	for (const key of getAutoFoldRanges(view.state).keys()) {
		knownKeys.add(key);
	}

	let foldTimer: ReturnType<typeof setTimeout> | null = null;

	return {
		update(update: import("@codemirror/view").ViewUpdate) {
			if (!update.docChanged) return;

			const isFullDocumentReplace = update.transactions.some(
				(tr) => tr.annotation(fullDocumentReplaceAnnotation) === true,
			);

			// Read fold blocks from the StateField (already recomputed on docChanged).
			const current = getAutoFoldRanges(update.state);
			if (isFullDocumentReplace) {
				knownKeys.clear();
				for (const key of current.keys()) knownKeys.add(key);
				return;
			}

			const newBlocks: { from: number; to: number }[] = [];

			for (const [key, range] of current) {
				if (!knownKeys.has(key)) {
					newBlocks.push(range);
				}
			}

			// Refresh known set
			knownKeys.clear();
			for (const key of current.keys()) knownKeys.add(key);

			if (newBlocks.length === 0) return;

			// Debounce — wait for snippet editing to finish
			if (foldTimer) clearTimeout(foldTimer);
			foldTimer = setTimeout(() => {
				foldTimer = null;
				// Only fold blocks that are still unfolded
				const folded = foldedRanges(update.view.state);
				const foldedFroms = new Set<number>();
				const iter = folded.iter();
				while (iter.value) {
					foldedFroms.add(iter.from);
					iter.next();
				}

				const effects: StateEffect<unknown>[] = [];
				for (const { from, to } of newBlocks) {
					if (!foldedFroms.has(from)) {
						effects.push(foldEffect.of({ from, to }));
					}
				}
				if (effects.length > 0) {
					update.view.dispatch({ effects });
				}
			}, 800);
		},

		destroy() {
			if (foldTimer) clearTimeout(foldTimer);
		},
	};
});
